// =============================================================================
// ChildUniformsSection.jsx
// =============================================================================
// Per-child uniform form section used inside the request wizard.
//
// Renders: supplier picker (School Locker / reimbursement / other), then a
//          UniformTable for item quantities. For some suppliers, captures
//          authorised-person contact details for the supplier to call. For
//          School Locker, also captures mobile + email for SMS pickup
//          notification and shows a "What is a SKU?" help modal.
//
// Props:
//   uniforms    - object ({ supplier, items, customItems, isReimbursement,
//                 isColdClimate, mobile, email, authorisedPerson,
//                 authorisedContact, approvalStatus, ... })
//   onChange    - called with the updated uniforms object
//   gender      - child's gender (passed through to UniformTable)
//   yearLevel   - child's year level (passed through to UniformTable)
//   schoolName  - child's school (used for School Locker URL link)
//   shelterName - shelter name (used for the printChild call)
//   refNo       - request ref (used for printChild call)
//   user        - current user; staff see extra accounting labels
//   hasError    - if true, highlight section border as invalid
//
// SEE ALSO
//   src/components/form/ChildSections.jsx — re-exports this component
//   src/hooks/useUniformSuppliers.ts       — admin-driven supplier list + capture flow (db-50)
// =============================================================================
import React from "react";
import { isResponderJob } from "../../app/access";
import { supplierMeta, useUniformSuppliers } from "../../hooks/useUniformSuppliers";
import { deriveSupplierType } from "../../services/approvals";
import { PANEL } from "../../utils/constants";
import { APPROVAL_STATUSES } from "../../utils/enums";
import { UNIFORM_ITEMS, WINTER_ITEMS } from "../../utils/formConstants";
import { schoolLockerUrl } from "../../utils/helpers";
import { ModalBackdrop } from "./FormUI.jsx";
import { HelpButton } from "./HelpButton.jsx";
import { PrintButton, printChild } from "./PrintButton.jsx";
import { UniformTable } from "./UniformTable.jsx";

function ChildUniformsSection({
  uniforms,
  onChange,
  gender,
  yearLevel,
  schoolName,
  shelterName,
  refNo,
  user,
  hasError,
}) {
  const mobileId = React.useId();
  const emailId = React.useId();
  const isStaff = isResponderJob(user);
  // The dropdown is admin-driven (Manage → Suppliers, db-46): the uniform-flagged rows,
  // already name-ordered with "Other Supplier" last by the repo.
  const { suppliers: uniformSuppliers } = useUniformSuppliers();

  const currentSupplier = uniforms ? uniforms.supplier || "" : "";
  // Capture flow is derived from the supplier ROW's billing fields (db-50) via
  // deriveSupplierType — NOT the supplier name, and NOT the Action Sheet override
  // (the override only affects next-steps wording, not how items are captured).
  // Unknown / not-yet-loaded supplier → null → neutral (no special panel fires).
  const selMeta = supplierMeta(uniformSuppliers, currentSupplier);
  const captureType = selMeta.kind
    ? deriveSupplierType({
        kind: selMeta.kind,
        payment_method: selMeta.paymentMethod,
        document_type: selMeta.documentType,
      })
    : null;
  const isSL = captureType === "school_locker";
  const isReimb = captureType === "reimbursement";
  // Authority suppliers (supplier invoices + Authorisation-to-Purchase) show the
  // authorised-person panel + Print Authorisation button.
  const isAuthoritySupplier = captureType === "authority";
  // "direct" = School / P&C Shop etc. → paid directly (SCH SO), no authority doc.
  const isSchool = captureType === "direct";

  function up(fields) {
    onChange(Object.assign({}, uniforms, fields));
  }

  const emptyCustomItems = [
    { name: "", sku: "", size: "", cost: "", qty: 0 },
    { name: "", sku: "", size: "", cost: "", qty: 0 },
    { name: "", sku: "", size: "", cost: "", qty: 0 },
    { name: "", sku: "", size: "", cost: "", qty: 0 },
    { name: "", sku: "", size: "", cost: "", qty: 0 },
  ];

  function changeSupplier(supplier) {
    const meta = supplierMeta(uniformSuppliers, supplier);
    const supType = meta.kind
      ? deriveSupplierType({
          kind: meta.kind,
          payment_method: meta.paymentMethod,
          document_type: meta.documentType,
        })
      : null;
    if (supType === "school_locker") {
      const slItems = {};
      [].concat(UNIFORM_ITEMS, WINTER_ITEMS).forEach(function (k) {
        slItems[k] = { sku: "", size: "", cost: "", qty: 0 };
      });
      onChange({
        supplier: supplier,
        isReimbursement: false,
        isColdClimate: uniforms ? uniforms.isColdClimate || false : false,
        mobile: "",
        email: "",
        other: "",
        items: slItems,
        customItems: emptyCustomItems,
        authorisedPerson: "",
        authorisedContact: "",
        approvalStatus: "Pending",
      });
    } else {
      const isR = supType === "reimbursement";
      const uniItems = {};
      [].concat(UNIFORM_ITEMS, WINTER_ITEMS).forEach(function (k) {
        uniItems[k] = { cost: "", qty: 0 };
      });
      onChange(
        Object.assign({}, uniforms || {}, {
          supplier: supplier,
          isReimbursement: isR,
          isColdClimate: uniforms ? uniforms.isColdClimate || false : false,
          other: "",
          items: uniforms?.items || uniItems,
          customItems: uniforms?.customItems || emptyCustomItems,
          authorisedPerson: uniforms ? uniforms.authorisedPerson || "" : "",
          authorisedContact: uniforms ? uniforms.authorisedContact || "" : "",
          approvalStatus: "Pending",
        })
      );
    }
  }

  const inp = {
    padding: "5px 8px",
    borderRadius: 5,
    border: "1px solid #CFD8DC",
    fontSize: 12,
    fontFamily: "inherit",
    background: "#fff",
  };

  const tableChild = Object.assign({}, uniforms || {}, {
    gender: gender,
    yearLevel: yearLevel,
    schoolName: schoolName,
  });

  // SKU help modal state — shown when user clicks "What is a SKU?" link
  const skuHelpState = React.useState(false);
  const showSkuHelp = skuHelpState[0];
  const setShowSkuHelp = skuHelpState[1];
  const slUrlForSchool = isSL ? schoolLockerUrl(schoolName) : null;

  // Prefill the School Locker SMS-pickup mobile + notification email from the
  // caseworker (editable). One-shot when School Locker is first selected; never
  // overwrites values the user has already entered.
  React.useEffect(
    function () {
      if (!isSL) return;
      const patch = {};
      if (!uniforms?.mobile && user?.contactMobile) patch.mobile = user.contactMobile;
      if (!uniforms?.email && user?.email) patch.email = user.email;
      if (Object.keys(patch).length > 0) up(patch);
    },
    [isSL]
  );

  return (
    <div
      style={{
        border: `1px solid ${hasError ? "#C62828" : "#CFE0DE"}`,
        borderRadius: 10,
        marginBottom: 14,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          background: hasError ? "#FFEBEE" : PANEL.bg,
          borderBottom: `1px solid ${hasError ? "#EF9A9A" : PANEL.border}`,
          padding: "10px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: 14,
            color: PANEL.heading,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          Uniforms
          {!isStaff && <HelpButton helpKey="uniforms" user={user} />}
          {isSL && (
            <button
              type="button"
              onClick={function () {
                setShowSkuHelp(true);
              }}
              style={{
                background: "#283593",
                border: "none",
                color: "white",
                borderRadius: 14,
                padding: "3px 12px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              ❓ What is a SKU?
            </button>
          )}
        </span>
        {isStaff && (
          <div style={{ fontSize: 12, color: PANEL.heading }}>
            {
              "Sales Order type follows the supplier's Type: School Locker — LOC SO · Authority-doc suppliers — Auth Doc · School / P&C — SCH SO · Reimbursement — REIMB SO."
            }
          </div>
        )}
      </div>
      <div style={{ padding: "12px 16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 14,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: "#1A2E2B", whiteSpace: "nowrap" }}>
            Supplier
          </span>
          <select
            value={currentSupplier}
            onChange={function (e) {
              changeSupplier(e.target.value);
            }}
            style={Object.assign({}, inp, {
              flex: "1 1 180px",
              minWidth: 160,
              fontSize: 13,
              borderColor: !currentSupplier ? "#FFB300" : "#CFD8DC",
              fontWeight: currentSupplier ? 600 : 400,
              color: currentSupplier ? "#00695C" : "#888",
            })}
          >
            <option value="">Select supplier *</option>
            {uniformSuppliers.map(function (s) {
              return React.createElement("option", { key: s.supplier_code, value: s.name }, s.name);
            })}
          </select>
          {/* Internal Zephyr accounting labels — hidden from shelter users */}
          {isStaff && isAuthoritySupplier && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                background: "#E8F5E9",
                color: "#2E7D32",
                border: "1px solid #A5D6A7",
                borderRadius: 4,
                padding: "2px 8px",
              }}
            >
              Auth Doc
            </span>
          )}
          {isStaff && isReimb && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                background: "#FFF3E0",
                color: "#E65100",
                border: "1px solid #FFB300",
                borderRadius: 4,
                padding: "2px 8px",
              }}
            >
              REIMB SO
            </span>
          )}
          {isStaff && isSchool && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                background: "#E3F2FD",
                color: "#0C447C",
                border: "1px solid #90CAF9",
                borderRadius: 4,
                padding: "2px 8px",
              }}
            >
              SCH SO
            </span>
          )}
          {currentSupplier && (
            <PrintButton
              onClick={function () {
                printChild({
                  child: tableChild,
                  childIndex: 0,
                  shelterName: shelterName || "Zephyr",
                  refNo: refNo || "Draft",
                  isSL: isSL,
                  shelterCode: user?.shelterCode || "",
                  shelterRef: "",
                  reqDate: new Date().toISOString().slice(0, 10),
                  totalChildren: 1,
                });
              }}
              disabled={uniforms?.approvalStatus !== "Approved"}
            />
          )}
        </div>

        {!currentSupplier && (
          <div style={{ color: "#607D79", fontSize: 13, fontStyle: "italic", padding: "8px 4px" }}>
            Select a supplier above to enter uniform details.
          </div>
        )}

        {currentSupplier && (
          <div>
            {isSL &&
              (function () {
                const mobileVal = uniforms?.mobile || "";
                const emailVal = uniforms?.email || "";
                const redBorder = "1px solid #C62828";
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: 8,
                      marginBottom: 10,
                      flexWrap: "wrap",
                      background: "#F5F7F6",
                      border: "1px solid #D0E4E0",
                      borderRadius: 8,
                      padding: "8px 12px",
                    }}
                  >
                    <div style={{ flex: "1 1 130px", minWidth: 120 }}>
                      <label
                        htmlFor={mobileId}
                        style={{
                          display: "block",
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#1A2E2B",
                          marginBottom: 3,
                        }}
                      >
                        Caseworker's Mobile (SMS pickup) <span style={{ color: "#C62828" }}>*</span>
                      </label>
                      <input
                        id={mobileId}
                        placeholder="e.g. 0400 123 456"
                        value={mobileVal}
                        onChange={function (e) {
                          up({ mobile: e.target.value });
                        }}
                        style={Object.assign({}, inp, {
                          width: "100%",
                          border: mobileVal ? inp.border : redBorder,
                        })}
                      />
                    </div>
                    <div style={{ flex: "1 1 150px", minWidth: 140 }}>
                      <label
                        htmlFor={emailId}
                        style={{
                          display: "block",
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#1A2E2B",
                          marginBottom: 3,
                        }}
                      >
                        Caseworker's Email (notification){" "}
                        <span style={{ color: "#C62828" }}>*</span>
                      </label>
                      <input
                        id={emailId}
                        placeholder="e.g. dfvservice@example.com"
                        value={emailVal}
                        onChange={function (e) {
                          up({ email: e.target.value });
                        }}
                        style={Object.assign({}, inp, {
                          width: "100%",
                          border: emailVal ? inp.border : redBorder,
                        })}
                      />
                    </div>
                    <label
                      style={{
                        fontSize: 12,
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        color: "#607D79",
                        whiteSpace: "nowrap",
                        paddingBottom: 8,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={uniforms?.isColdClimate || false}
                        onChange={function (e) {
                          up({ isColdClimate: e.target.checked });
                        }}
                      />
                      Cold Climate
                      <HelpButton helpKey="cold_climate" user={user} />
                    </label>
                  </div>
                );
              })()}

            {!isSL && (
              <div>
                {isReimb && isStaff && (
                  <div
                    style={{
                      background: "#FFF3E0",
                      border: "1px solid #FFB300",
                      borderRadius: 7,
                      padding: "8px 12px",
                      marginBottom: 10,
                      fontSize: 13,
                      color: "#E65100",
                    }}
                  >
                    {currentSupplier +
                      " — the DFV Service purchases items and Zephyr reimburses. Items will be added to the reimbursement Sales Order."}
                  </div>
                )}
                {isSchool && isStaff && (
                  <div
                    style={{
                      background: "#E3F2FD",
                      border: "1px solid #90CAF9",
                      borderRadius: 7,
                      padding: "8px 12px",
                      marginBottom: 10,
                      fontSize: 13,
                      color: "#0C447C",
                    }}
                  >
                    School — payment made directly to the school (SCH SO).
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 10,
                    flexWrap: "wrap",
                    background: "#F5F7F6",
                    border: "1px solid #D0E4E0",
                    borderRadius: 8,
                    padding: "8px 12px",
                  }}
                >
                  <label
                    style={{
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      color: "#607D79",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={uniforms?.isColdClimate || false}
                      onChange={function (e) {
                        up({ isColdClimate: e.target.checked });
                      }}
                    />
                    Cold Climate
                    <HelpButton helpKey="cold_climate" user={user} />
                  </label>
                </div>
              </div>
            )}

            {isAuthoritySupplier &&
              (isStaff || (uniforms && uniforms.approvalStatus === APPROVAL_STATUSES.APPROVED)) && (
                <div
                  style={{
                    padding: "10px 12px",
                    background: "#F5F5F5",
                    border: "1px solid #BDBDBD",
                    borderRadius: 8,
                    marginBottom: 10,
                    opacity: 0.75,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#757575",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      marginBottom: 4,
                    }}
                  >
                    Authority Document Details
                  </div>
                  <div
                    style={{ fontSize: 11, color: "#9E9E9E", marginBottom: 8, fontStyle: "italic" }}
                  >
                    Complete these fields when approval is received — do not record names until
                    then.
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 200px" }}>
                      <div style={{ fontSize: 11, color: "#9E9E9E", marginBottom: 3 }}>
                        Name of person authorised to collect
                      </div>
                      <input
                        type="text"
                        value={uniforms?.authorisedPerson || ""}
                        placeholder="Complete after approval"
                        onChange={function (e) {
                          up({ authorisedPerson: e.target.value });
                        }}
                        style={Object.assign({}, inp, {
                          width: "100%",
                          fontSize: 13,
                          background: "#F5F5F5",
                          color: "#757575",
                        })}
                      />
                    </div>
                    <div style={{ flex: "1 1 200px" }}>
                      <div style={{ fontSize: 11, color: "#9E9E9E", marginBottom: 3 }}>
                        Contact number
                      </div>
                      <input
                        type="text"
                        value={uniforms?.authorisedContact || ""}
                        placeholder="Complete after approval"
                        onChange={function (e) {
                          up({ authorisedContact: e.target.value });
                        }}
                        style={Object.assign({}, inp, {
                          width: "100%",
                          fontSize: 13,
                          background: "#F5F5F5",
                          color: "#757575",
                        })}
                      />
                    </div>
                  </div>
                </div>
              )}

            <UniformTable
              child={tableChild}
              onChange={function (v) {
                up({ items: v.items, customItems: v.customItems });
              }}
              showSku={isSL}
              requireCustomName={!isSL}
              isCold={uniforms?.isColdClimate || false}
            />
          </div>
        )}
      </div>
      {showSkuHelp && (
        <ModalBackdrop
          onClose={function () {
            setShowSkuHelp(false);
          }}
          contentStyle={{
            background: "white",
            borderRadius: 10,
            maxWidth: 480,
            width: "calc(100vw - 40px)",
            padding: 24,
            boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 18, color: "#283593", marginBottom: 12 }}>
            What is a SKU?
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.5, color: "#1A2E2B", marginTop: 0 }}>
            A SKU is a stock code for the item you wish to purchase. Go to the School Locker website
            for your school and find the SKU code for the items you want (you may need to open each
            item to see its SKU).
          </p>
          {slUrlForSchool ? (
            <button
              type="button"
              onClick={function () {
                window.open(slUrlForSchool, "_blank", "noopener,noreferrer");
                setShowSkuHelp(false);
              }}
              style={{
                display: "block",
                width: "100%",
                marginTop: 16,
                padding: "10px 14px",
                background: "#00695C",
                color: "white",
                border: "none",
                borderRadius: 7,
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {"Browse " +
                (schoolName
                  ? String(schoolName)
                      .split(/\s[—-]\s/)[0]
                      .split(/\s\(/)[0]
                  : "this school") +
                " on School Locker →"}
            </button>
          ) : (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                background: "#FFF8E1",
                border: "1px solid #FFB300",
                borderRadius: 7,
                fontSize: 13,
                color: "#7c4a00",
              }}
            >
              Enter the child's school name in the General Info tab to enable a direct link.
            </div>
          )}
          <button
            type="button"
            onClick={function () {
              setShowSkuHelp(false);
            }}
            style={{
              display: "block",
              width: "100%",
              marginTop: 8,
              padding: "8px 14px",
              background: "none",
              color: "#607D79",
              border: "1px solid #CFE0DE",
              borderRadius: 7,
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Close
          </button>
        </ModalBackdrop>
      )}
    </div>
  );
}

export { ChildUniformsSection };
